import type { ReactNode, ComponentType } from 'react';
import { useEffect, useState } from 'react';
import SideNav from '../SideNav';
import MobileHeader from '../MobileHeader';
import Footer from '../../shared/Footer';

type LayoutProps = {
  children: ReactNode;
};

export default function Layout({ children }: LayoutProps) {
  const [vercel, setVercel] = useState<null | {
    Analytics: ComponentType;
    SpeedInsights: ComponentType;
  }>(null);

  useEffect(() => {
    let cancelled = false;

    const loadVercel = async () => {
      try {
        const [{ Analytics }, { SpeedInsights }] = await Promise.all([
          import('@vercel/analytics/react'),
          import('@vercel/speed-insights/react'),
        ]);

        if (!cancelled) {
          setVercel({ Analytics, SpeedInsights });
        }
      } catch {
        // If the dynamic import fails for any reason, we silently skip.
        // Analytics/insights should never block rendering.
      }
    };

    const scheduleLoad = () => {
      const ric = (
        window as unknown as {
          requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
        }
      ).requestIdleCallback;

      if (typeof ric === 'function') {
        ric(
          () => {
            void loadVercel();
          },
          { timeout: 2000 },
        );
      } else {
        // Fallback: next tick after load
        window.setTimeout(() => {
          void loadVercel();
        }, 0);
      }
    };

    if (document.readyState === 'complete') {
      scheduleLoad();
      return () => {
        cancelled = true;
      };
    }

    window.addEventListener('load', scheduleLoad, { once: true });

    return () => {
      cancelled = true;
      window.removeEventListener('load', scheduleLoad);
    };
  }, []);

  return (
    <div className="text-[var(--color-text)] min-h-screen bg-[var(--color-background)]">
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-[var(--color-primary)] focus:text-white focus:rounded focus:outline-2 focus:outline-offset-2 focus:outline-[var(--color-primary)]"
      >
        Skip to main content
      </a>

      <MobileHeader />
      <div className="flex justify-center px-3 sm:px-4 md:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:gap-x-4 w-full max-w-7xl lg:min-h-screen">
          <div className="hidden lg:block w-64 2xl:w-72 flex-shrink-0">
            <div className="sticky-sidebar">
              <SideNav />
            </div>
          </div>
          <main
            id="main-content"
            tabIndex={-1}
            className="min-w-0 flex-1 pt-6 sm:pt-8 lg:pt-12"
            role="main"
            aria-label="Main content"
          >
            {children}
            <Footer />
          </main>

          {/* Loaded after `load`/idle so it doesn't affect first paint/LCP */}
          {vercel ? (
            <>
              <vercel.Analytics />
              <vercel.SpeedInsights />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
